import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Divider, Radio } from 'antd';

import ProfitCapitalizationComponent from '../components/profitCapitalizationForm';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ComponentHeader from 'components/componentHeader/componentHeader';
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';

import NotificationService from 'core/services/notification';
import { onGetRaiseCapital } from '../services/raiseCapitals';

import { RaiseCapitalTypeKey } from '../models/raiseCapitals';
import { ProfitCapitalization } from '../models/profitCapitalization';
import TreasurySharesAcquisitionForm from '../components/treasurySharesAcquisitionForm';
import { TreasurySharesAcquisition } from '../models/treasurySharesAcquisition';
import { PrivateEquityIssuance } from '../models/privateEquityIssuance';
import PrivateEquityIssuanceForm from '../components/PrivateEquityForm';

const RaiseCapitalsEditPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [capitalTypeOptions, setCapitalTypeOptions] = useState<Array<{ value: string; label: JSX.Element }>>();
	const [capitalType, setCapitalType] = useState<RaiseCapitalTypeKey>();
	const [raiseCapital, setRaiseCapital] = useState<ProfitCapitalization | TreasurySharesAcquisition | PrivateEquityIssuance>();

	useEffect(() => {
		getRaiseCapitalDetails();
	}, []);

	const getRaiseCapitalDetails = async () => {
		try {
			setIsLoading(true);

			const response = await onGetRaiseCapital(Number(id), 1);

			setRaiseCapital(response);
			setCapitalType(response.action.type_key);
			setCapitalTypeOptions([
				{
					value: response.action.type_key,
					label: (
						<>
							<p className="mb-0">
								{response.action.type_key === 'ProfitCapitalization'
									? t('ACTIONS.PROFIT_CAPITALIZATION')
									: response.action.type_key === 'TreasurySharesAcquisition'
									? t('ACTIONS.TREASURY_SHARES_ACQUISITION')
									: t('ACTIONS.PRIVATE_EQUITY_ISSUANCE')}
							</p>
							<p className="mb-0 text-light">
								{response.action.type_key === 'ProfitCapitalization'
									? t('ACTIONS.PROFIT_CAPITALIZATION_DESC')
									: response.action.type_key === 'TreasurySharesAcquisition'
									? t('ACTIONS.TREASURY_SHARES_ACQUISITION_DESC')
									: t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_DESC')}
							</p>
						</>
					),
				},
			]);

			setIsLoading(false);
		} catch (error: any) {
			setIsLoading(false);
			const message = error?.response?.data?.message || t('COMMON.FETCH_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);

			navigate('/dashboard/actions/capitals');
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<ComponentHeader title={t('ACTIONS.RAISE_CAPITAL')} subtitle={t('ACTIONS.FILL_THE_FOLLOWING_TO_PROCESS_REQUEST')} />

					<h6 className="fw-medium">{t('ACTIONS.TYPE_OF_RAISE')}</h6>

					<Radio.Group
						className="customRadio"
						style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
						options={capitalTypeOptions}
						defaultValue={capitalType}
					/>

					<Divider />

					{capitalType === 'ProfitCapitalization' && (
						<ProfitCapitalizationComponent editMode profitCapitalization={raiseCapital as ProfitCapitalization} />
					)}

					{capitalType === 'TreasurySharesAcquisition' && (
						<TreasurySharesAcquisitionForm editMode treasurySharesAcquisition={raiseCapital as TreasurySharesAcquisition} />
					)}

					{capitalType === 'PrivateEquityIssuance' && (
						<PrivateEquityIssuanceForm editMode privateEquityIssuance={raiseCapital as PrivateEquityIssuance} />
					)}
				</Card>
			)}
		</>
	);
};

export default RaiseCapitalsEditPage;
